import { type NavigateOptions, useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useCommitees } from '@/api/commitee/commitee.queries';
import { useAuth } from '@/context/useAuth';
import { slugify } from './string.utils';

export enum CommiteeIdResolutionStatus {
  LOADING = 'loading',
  NOT_FOUND = 'not-found',
  READY = 'ready',
}

type CommiteeIdResolution =
  | { status: CommiteeIdResolutionStatus.LOADING }
  | { status: CommiteeIdResolutionStatus.NOT_FOUND }
  | { status: CommiteeIdResolutionStatus.READY; commiteeId: number };

export function useOptionalRouteId(params: Record<string, unknown>) {
  const stringId = params.id;
  if (typeof stringId === 'string') {
    const number = +stringId;
    if (Number.isNaN(number)) return undefined;
    return number;
  }
  return undefined;
}

export function useCommiteeSlugFromUrl(): string | undefined {
  const params = useParams({ strict: false });
  return typeof params.commiteeSlug === 'string' ? params.commiteeSlug : undefined;
}

export function useCommiteeSlugNavigate(): (options: NavigateOptions) => void {
  const navigate = useNavigate();
  const { session } = useAuth();
  const commiteeSlugFromUrl = useCommiteeSlugFromUrl();

  return useCallback(
    (options: NavigateOptions) => {
      const paramsCommiteeSlug =
        options.params &&
        typeof options.params === 'object' &&
        !Array.isArray(options.params) &&
        typeof options.params.commiteeSlug === 'string'
          ? options.params.commiteeSlug
          : undefined;

      const commiteeSlug = paramsCommiteeSlug || commiteeSlugFromUrl || session?.user?.commiteeSlug;

      if (!commiteeSlug) {
        return;
      }

      navigate({
        ...options,
        params: {
          ...(options.params && typeof options.params === 'object' && !Array.isArray(options.params)
            ? options.params
            : {}),
          commiteeSlug: commiteeSlug,
        },
      });
    },
    [navigate, commiteeSlugFromUrl, session?.user?.commiteeSlug]
  );
}

export function useNavigateAfterLogin() {
  const { session, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navigateWithCommiteeSlug = useCommiteeSlugNavigate();
  const isLoginPage = location.pathname === '/login';
  const isResetPasswordPage = location.pathname === '/reset-password';

  return () => {
    if (!session) {
      if (!isLoginPage && !isResetPasswordPage) {
        navigate({ to: '/login', replace: true });
      }
      return;
    }

    if (isAdmin) {
      navigate({ to: '/gestion/general', replace: true });
    } else {
      navigateWithCommiteeSlug({ to: '/$commiteeSlug', replace: true });
    }
    return;
  };
}

export function useCommiteeIdResolutionFromUrl(): CommiteeIdResolution {
  const params = useParams({ strict: false });
  const { data: commitees } = useCommitees();

  return useMemo(() => {
    if (commitees === undefined) {
      return { status: CommiteeIdResolutionStatus.LOADING } as const;
    }
    const commiteeId = commitees.find(
      (commitee) => slugify(commitee.name) === params.commiteeSlug
    )?.id;
    if (commiteeId === undefined) {
      return { status: CommiteeIdResolutionStatus.NOT_FOUND } as const;
    }
    return { status: CommiteeIdResolutionStatus.READY, commiteeId } as const;
  }, [commitees, params.commiteeSlug]);
}

export function useCommiteeIdFromUrl(): number | undefined {
  const commiteeIdResolution = useCommiteeIdResolutionFromUrl();
  return commiteeIdResolution.status === CommiteeIdResolutionStatus.READY
    ? commiteeIdResolution.commiteeId
    : undefined;
}

export function useCommiteeId(): number | undefined {
  const { session, isAdmin } = useAuth();
  const params = useParams({ strict: false });
  const commiteeIdFromUrl = useCommiteeIdFromUrl();

  if (isAdmin) {
    if (params.id) {
      const id = Number.parseInt(params.id as string, 10);
      if (!Number.isNaN(id)) {
        return id;
      }
    }
    return commiteeIdFromUrl;
  }
  return session?.user?.commiteeSlugId || commiteeIdFromUrl;
}
